import Box from '@mui/material/Box';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { PaymentFormValues } from 'src/pages/payment-msi/PaymentMsiV2/PaymentMsiV2';
import { PaymentTextField } from 'src/pages/payment-msi/PaymentMsiV2/components/PaymentTextField';
import { StatesAutocomplete } from 'src/pages/payment-msi/PaymentMsiV2/components/StatesAutocomplete';
import { BillingAddress } from 'src/pages/payment-msi/PaymentMsiV2/types/billing-address.type';
import { AppTheme } from 'src/themes/muiTheme';
import { FormRow } from './FormRow';
import { PaymentSection, PaymentSectionHeader } from './PaymentSection';

const billingAddressStyles = {
  sectionHeader: (theme: AppTheme) => {
    const mobileMaxSize = theme.breakpoints.values.sm + 1;
    const mobile = theme.breakpoints.down(mobileMaxSize);

    return {
      [mobile]: {
        display: 'flex',
        justifyContent: 'space-between',
      },
    };
  },
  sectionHeaderTitle: (theme: AppTheme) => {
    const mobileMaxSize = theme.breakpoints.values.sm + 1;
    const mobile = theme.breakpoints.down(mobileMaxSize);

    return {
      [mobile]: {
        marginBottom: '0',
        fontSize: '16px',
      },
    };
  },
  formFieldsWrapper: (theme: AppTheme) => {
    const mobileMaxSize = theme.breakpoints.values.sm + 1;
    const mobile = theme.breakpoints.down(mobileMaxSize);

    return {
      marginTop: '40px',
      display: 'flex',
      flexDirection: 'column',
      rowGap: '24px',
      [mobile]: {
        marginTop: '20px',
      },
    };
  },
};

interface BillingAddressSectionProps {
  buildingAddress: BillingAddress;
}

export function BillingAddressSection({
  buildingAddress,
}: BillingAddressSectionProps) {
  const [isSwitchActive, setIsSwitchActive] = useState(true);

  const {
    setValue,
    control,
    formState: { errors },
  } = useFormContext<PaymentFormValues>();

  function onPrefillAddress() {
    setIsSwitchActive((prevState) => {
      const newSwitchState = !prevState;

      if (newSwitchState) {
        setValue('billingAddress', buildingAddress);
      } else {
        setValue('billingAddress', {
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          zipCode: '',
        });
      }

      return newSwitchState;
    });
  }

  return (
    <PaymentSection>
      <PaymentSectionHeader
        hasSwitch={true}
        sectionTitle={'Billing address is same as coverage address'}
        onSwitchChange={onPrefillAddress}
        switchOpen={isSwitchActive}
        headerProps={{
          sx: billingAddressStyles.sectionHeader,
        }}
        sectionTitleProps={{
          sx: billingAddressStyles.sectionHeaderTitle,
        }}
      />
      <Box sx={billingAddressStyles.formFieldsWrapper}>
        <PaymentTextField
          name="billingAddress.addressLine1"
          label="Address Line 1"
          required={true}
          disabled={isSwitchActive}
          error={Boolean(errors.billingAddress?.addressLine1)}
          helperText={errors.billingAddress?.addressLine1?.message}
        />
        <FormRow>
          <PaymentTextField
            name="billingAddress.addressLine2"
            label="Address Line 2"
            disabled={isSwitchActive}
          />
          <PaymentTextField
            name="billingAddress.city"
            label="City"
            disabled={isSwitchActive}
            required={true}
            error={Boolean(errors.billingAddress?.city)}
            helperText={errors.billingAddress?.city?.message}
          />
        </FormRow>
        <FormRow>
          <Controller
            control={control}
            name="billingAddress.state"
            render={({ field }) => {
              return (
                <StatesAutocomplete
                  label="State"
                  disabled={isSwitchActive}
                  name={field.name}
                  selectedState={field.value}
                  onStateChange={field.onChange}
                  required={true}
                  error={Boolean(errors.billingAddress?.state)}
                  helperText={errors.billingAddress?.state?.message}
                />
              );
            }}
          />
          <PaymentTextField
            name="billingAddress.zipCode"
            label="Zip Code"
            required={true}
            disabled={isSwitchActive}
            error={Boolean(errors.billingAddress?.zipCode)}
            helperText={errors.billingAddress?.zipCode?.message}
          />
        </FormRow>
      </Box>
    </PaymentSection>
  );
}
